import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DragEvent, MutableRefObject } from "react";
import type { Refs, Row, TabDef } from "../types";
import { api, ApiError } from "../api";
import type { LeituraIA, VerificacaoData } from "../api";
import { formatCurrency, formatDate, statusTone } from "../format";
import RecordForm from "./RecordForm";
import ErrorState from "./ErrorState";
import { IconSearch, IconPlus, IconEdit, IconTrash, IconClose, IconCalendar, IconCheck, IconRefresh, IconDownload } from "../icons";
import { usePodeExcluir } from "../permissoes";
import { compararNomes } from "../ordem";
import { COLUNAS_KANBAN, colunaDaAtividade, ehColunaKanban, type ColunaKanban } from "../kanban";

interface Props {
  tab: TabDef;
  refs: Refs;
  onDataChanged: () => void;
  /** Somente visualização (Portal): o quadro não deixa arrastar nem mover. */
  somenteLeitura?: boolean;
}

/** Campos que decidem a coluna do cartão (ver src/kanban.ts). */
const CAMPOS_QUADRO = ["etapa_omie", "kanban_coluna", "kanban_etapa_ref", "kanban_movido_em", "kanban_movido_por"];

/** Kanban (padrão) ou a Lista de sempre — lembrado por navegador. */
type Visao = "kanban" | "lista";
const CHAVE_VISAO = "planejamento.atividades.visao";

function lerVisao(): Visao {
  try {
    return localStorage.getItem(CHAVE_VISAO) === "lista" ? "lista" : "kanban";
  } catch {
    return "kanban"; // janela anônima / armazenamento bloqueado
  }
}

function gravarVisao(v: Visao) {
  try {
    localStorage.setItem(CHAVE_VISAO, v);
  } catch {
    /* sem armazenamento: só não lembra na próxima vez */
  }
}

type CkptResumo = {
  atividade_id: number;
  data_sugerida: string | null;
  data_confirmada?: string | null;
  opcao_escolhida?: string | null;
  preenchido_em: string;
};

/** Partículas do português que acompanham o sobrenome (ficam minúsculas, não viram inicial). */
const PARTICULAS = new Set(["de", "da", "do", "das", "dos", "e"]);

/**
 * Abrevia nomes longos respeitando a ordem do português: mantém o PRIMEIRO nome
 * e o ÚLTIMO sobrenome (com a partícula, ex.: "de Lima") e reduz os nomes do meio
 * a iniciais. Ex.: "Nicolas Henrique Vanderlei de Lima" -> "Nicolas H. V. de Lima".
 * Nomes curtos (até 20 caracteres) ou com até 2 palavras ficam inteiros.
 */
function abreviarNome(nome: string): string {
  const limpo = (nome || "").trim().replace(/\s+/g, " ");
  if (limpo.length <= 20) return limpo;
  const partes = limpo.split(" ");
  if (partes.length <= 2) return limpo;

  const primeiro = partes[0];
  let fim = partes.length - 1;
  let sobrenome = partes[fim];
  // junta a partícula anterior ao sobrenome final ("de Lima", "dos Santos").
  if (fim - 1 >= 1 && PARTICULAS.has(partes[fim - 1].toLowerCase())) {
    sobrenome = `${partes[fim - 1].toLowerCase()} ${sobrenome}`;
    fim -= 1;
  }
  const iniciais = partes
    .slice(1, fim)
    .filter((p) => !PARTICULAS.has(p.toLowerCase()))
    .map((p) => `${p.charAt(0).toUpperCase()}.`)
    .join(" ");
  return [primeiro, iniciais, sobrenome].filter(Boolean).join(" ");
}

type OmieStatus = {
  configurado: boolean;
  sincronizando: boolean;
  ultima_sincronizacao: string | null;
  total_os_omie: number | null;
  ativas: number;
};

/** "última 14:32" se hoje; "última 08/07 14:32" caso contrário. */
function fmtSync(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const hoje = new Date();
  const mesmoDia = d.toDateString() === hoje.toDateString();
  const hora = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  return mesmoDia ? hora : `${d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })} ${hora}`;
}

type Opcao = {
  indice: number;
  inicio: string;
  fim: string;
  dias_uteis: string[];
  equipe: { id: number; nome: string }[];
  rotulo: string;
};
type DocInfo = {
  ok: boolean;
  encontradoNoDp?: boolean;
  colaborador?: string;
  presentes?: { doc: string; arquivo: string }[];
  faltando?: string[];
  mensagem: string;
};
/** Lê opcao_manual (jsonb objeto ou string JSON) — a data escolhida à mão. */
function lerOpcaoManual(raw: unknown): Opcao | null {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) return raw as Opcao;
  if (typeof raw === "string") {
    try {
      const v = JSON.parse(raw);
      return v && typeof v === "object" && !Array.isArray(v) ? (v as Opcao) : null;
    } catch {
      return null;
    }
  }
  return null;
}

/** Lê opcoes_data (jsonb array ou string JSON). */
function lerOpcoes(raw: unknown): Opcao[] {
  if (Array.isArray(raw)) return raw as Opcao[];
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * Atividades (Planejamento) — em KANBAN (padrão: Ordem de Serviço → Em
 * execução → Retrabalho → Faturar, arrastando o cartão) ou em LISTA (tabela
 * com cliente, cidade, responsável técnico + telefone/e-mail, valores, prazos
 * e a data definida com o cliente). As duas usam a mesma busca e os mesmos
 * filtros. DOIS CLIQUES em um cartão/linha abrem o CHECKPOINT (perguntas para
 * planejar o serviço) e a geração da opção de data.
 */
export default function AtividadesView({ tab, refs, onDataChanged, somenteLeitura = false }: Props) {
  // Excluir atividade é só para administrador (regra do Portal).
  const podeExcluir = usePodeExcluir();
  const [visao, setVisao] = useState<Visao>(lerVisao);
  const [rows, setRows] = useState<Row[]>([]);
  const [ckpts, setCkpts] = useState<Map<number, CkptResumo>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [search, setSearch] = useState("");
  const [filtroVendedor, setFiltroVendedor] = useState("");
  const [filtroResponsavel, setFiltroResponsavel] = useState("");
  const [editing, setEditing] = useState<Row | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [checkpointFor, setCheckpointFor] = useState<Row | null>(null);
  const [importando, setImportando] = useState(false);
  const [omie, setOmie] = useState<OmieStatus | null>(null);

  // Mapa id -> colaborador (para mostrar responsável + telefone + e-mail).
  const colabMap = useMemo(() => {
    const m = new Map<number, Row>();
    for (const c of refs.colaboradores) m.set(c.id, c);
    return m;
  }, [refs.colaboradores]);

  /** Já carregou uma vez? Daí em diante a lista se atualiza sem piscar. */
  const carregouRef = useRef(false);
  /** Cartão sendo arrastado agora (id) — a atualização automática espera. */
  const arrastandoRef = useRef<number | null>(null);
  /** Cartões com a troca de coluna ainda a caminho do servidor. */
  const movendoRef = useRef<Set<number>>(new Set());
  /** Conta as trocas de coluna: lista pedida ANTES de uma delas já nasce velha. */
  const movimentosRef = useRef(0);

  const load = useCallback(async () => {
    // Só a PRIMEIRA carga mostra "Carregando...": a de 60 s (e a que vem
    // depois de salvar) troca os dados por baixo — antes a tela piscava.
    if (!carregouRef.current) setLoading(true);
    setError(null);
    const marca = movimentosRef.current;
    const emVoo = movendoRef.current.size > 0;
    try {
      const [atvs, cps, st] = await Promise.all([
        api.list(tab.resource),
        api.checkpoint.list().catch(() => [] as CkptResumo[]),
        api.omieStatus().catch(() => null),
      ]);
      // Um cartão mudou de coluna enquanto esta lista vinha (ou ainda estava
      // salvando quando ela foi pedida): a lista pode ser de ANTES da troca e
      // desfaria o cartão na tela. O resto dela entra; os campos do quadro
      // ficam os da tela, que já têm a troca.
      const corrida = emVoo || marca !== movimentosRef.current || movendoRef.current.size > 0;
      if (!corrida) setRows(atvs);
      else
        setRows((atual) => {
          const naTela = new Map(atual.map((r) => [r.id, r]));
          return atvs.map((r) => {
            const t = naTela.get(r.id);
            if (!t) return r;
            const juntos: Row = { ...r };
            for (const k of CAMPOS_QUADRO) juntos[k] = t[k];
            return juntos;
          });
        });
      setCkpts(new Map(cps.map((c) => [c.atividade_id, c])));
      setOmie(st);
      carregouRef.current = true;
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }, [tab.resource]);

  useEffect(() => {
    setSearch("");
    load();
  }, [load]);

  // Atualiza sozinho: a sincronização com o OMIE roda em segundo plano no servidor.
  useEffect(() => {
    const id = setInterval(() => {
      // No meio de um arraste (ou com cartão ainda salvando) a lista nova faria
      // o cartão pular de lugar — espera o próximo minuto.
      if (arrastandoRef.current !== null || movendoRef.current.size > 0) return;
      load();
    }, 60_000);
    return () => clearInterval(id);
  }, [load]);

  function trocarVisao(v: Visao) {
    setVisao(v);
    gravarVisao(v);
  }

  /**
   * Muda o cartão de coluna. A tela já mostra na coluna nova (otimista); se o
   * servidor recusar, o cartão volta para onde estava e o motivo aparece.
   * Grava só aqui no Planejamento — o OMIE não é alterado.
   */
  async function moverPara(id: number, coluna: ColunaKanban) {
    if (somenteLeitura || movendoRef.current.has(id)) return;
    const atual = rows.find((r) => r.id === id);
    // Soltar na mesma coluna não é mudança nenhuma.
    if (!atual || colunaDaAtividade(atual) === coluna) return;
    const antes: Row = {
      kanban_coluna: atual.kanban_coluna ?? null,
      kanban_etapa_ref: atual.kanban_etapa_ref ?? null,
      kanban_movido_em: atual.kanban_movido_em ?? null,
      kanban_movido_por: atual.kanban_movido_por ?? null,
    };
    // Sempre sobre a lista MAIS NOVA (setRows funcional): entre o arraste e a
    // resposta, outra coisa pode ter mexido nela.
    const juntar = (campos: Row) =>
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...campos } : r)));

    movimentosRef.current += 1;
    movendoRef.current.add(id);
    juntar({ kanban_coluna: coluna, kanban_etapa_ref: atual.etapa_omie ?? null });
    try {
      juntar(await api.moverKanban(id, coluna));
    } catch (err) {
      juntar(antes);
      alert(err instanceof Error ? err.message : "Erro ao mover a atividade");
    } finally {
      movendoRef.current.delete(id);
    }
  }

  // Filtros de vendedor e responsável em ordem alfabética (regra de src/ordem.ts).
  const vendedores = useMemo(
    () =>
      [...new Set(rows.map((r) => r.vendedor).filter(Boolean) as string[])].sort(compararNomes),
    [rows],
  );
  const responsaveis = useMemo(
    () =>
      [
        ...new Set(
          rows
            .map((r) => (r.colaborador_id ? colabMap.get(r.colaborador_id)?.nome : null))
            .filter(Boolean) as string[],
        ),
      ].sort(compararNomes),
    [rows, colabMap],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const colab = r.colaborador_id ? colabMap.get(r.colaborador_id) : null;
      if (filtroVendedor && r.vendedor !== filtroVendedor) return false;
      if (filtroResponsavel && colab?.nome !== filtroResponsavel) return false;
      if (q) {
        const hay = [r.cliente, r.cidade, r.nome, r.status, colab?.nome, r.vendedor]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, colabMap, filtroVendedor, filtroResponsavel]);

  function openNew() {
    setEditing(null);
    setFormOpen(true);
  }
  function openEdit(row: Row) {
    setEditing(row);
    setFormOpen(true);
  }

  async function handleSubmit(data: Row) {
    if (editing) await api.update(tab.resource, editing.id, data);
    else await api.create(tab.resource, data);
    setFormOpen(false);
    await load();
    onDataChanged();
  }

  async function handleDelete(row: Row) {
    if (!confirm("Excluir esta atividade? O checkpoint dela também será removido.")) return;
    try {
      await api.remove(tab.resource, row.id);
      await load();
      onDataChanged();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao excluir");
    }
  }

  async function importarDoOmie() {
    if (!confirm("Importar as Ordens de Serviço mais recentes do OMIE para as Atividades?\n\n(Os campos de planejamento já preenchidos — responsável, custo, prazos — são preservados.)"))
      return;
    setImportando(true);
    try {
      const r = await api.importarOmie(2);
      await load();
      onDataChanged();
      alert(
        `Importação do OMIE concluída.\n\nNovas: ${r.inseridas}\nAtualizadas: ${r.atualizadas}\nArquivadas (Faturadas no OMIE): ${r.arquivadas}\nFora da carteira (ignoradas): ${r.ignoradas}\nProcessadas: ${r.total}\nTotal de OS no OMIE: ${r.total_os_omie}`,
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao importar do OMIE");
    } finally {
      setImportando(false);
    }
  }

  if (error) return <ErrorState message={error.message} code={error.code} onRetry={load} />;

  return (
    <div className="crud">
      <div className="crud-toolbar">
        <div className="atv-filtros">
          <div className="atv-visao" role="group" aria-label="Forma de exibir as atividades">
            {(["kanban", "lista"] as const).map((v) => (
              <button
                key={v}
                type="button"
                className={visao === v ? "ativo" : ""}
                aria-pressed={visao === v}
                onClick={() => trocarVisao(v)}
              >
                {v === "kanban" ? "Kanban" : "Lista"}
              </button>
            ))}
          </div>
          <div className="search-wrap">
            <IconSearch size={17} />
            <input
              className="search"
              placeholder="Buscar cliente, cidade, atividade..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select className="atv-filtro" value={filtroVendedor} onChange={(e) => setFiltroVendedor(e.target.value)}>
            <option value="">Todos os vendedores</option>
            {vendedores.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
          <select
            className="atv-filtro"
            value={filtroResponsavel}
            onChange={(e) => setFiltroResponsavel(e.target.value)}
          >
            <option value="">Todos os responsáveis</option>
            {responsaveis.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        <div className="toolbar-right">
          <span className="badge">{filtered.length} atividade(s)</span>
          {omie && (
            <span
              className={`omie-status ${omie.configurado ? (omie.sincronizando || importando ? "sync" : "ok") : "off"}`}
              title={
                omie.configurado
                  ? `Atividades integradas ao OMIE. ${omie.ativas} OS na carteira${omie.total_os_omie != null ? ` de ${omie.total_os_omie} no OMIE` : ""}. Sincroniza sozinho ao iniciar e a cada 15 min.`
                  : "Integração OMIE não configurada (OMIE_APP_KEY/SECRET no .env)."
              }
            >
              <span className="omie-dot" />
              OMIE
              {omie.configurado
                ? omie.sincronizando || importando
                  ? " · sincronizando…"
                  : ` · ${omie.ativas} na carteira · ${fmtSync(omie.ultima_sincronizacao)}`
                : " · não configurado"}
            </span>
          )}
          <button className="btn-secondary" onClick={importarDoOmie} disabled={importando} title="Puxar as Ordens de Serviço do OMIE">
            <IconRefresh size={16} /> {importando ? "Importando..." : "Importar do OMIE"}
          </button>
          <button className="btn-primary" onClick={openNew}>
            <IconPlus size={17} /> Nova
          </button>
        </div>
      </div>

      <p className="muted doc-hint">
        Dê <strong>dois cliques</strong> em uma atividade para abrir o checkpoint e gerar a opção de data.
        {visao === "kanban" && !somenteLeitura && (
          <>
            {" "}
            <strong>Arraste</strong> o cartão para outra coluna para mudar a etapa — vale só aqui no
            Planejamento; o OMIE não é alterado.
          </>
        )}
      </p>

      {visao === "kanban" && (
        <QuadroKanban
          rows={filtered}
          carregando={loading}
          colabMap={colabMap}
          ckpts={ckpts}
          podeGravar={!somenteLeitura}
          podeExcluir={podeExcluir}
          arrastandoRef={arrastandoRef}
          onMover={moverPara}
          onEditar={openEdit}
          onExcluir={handleDelete}
          onCheckpoint={setCheckpointFor}
        />
      )}

      {visao === "lista" && (
        <div className="table-wrap atv-scroll">
          {loading ? (
            <div className="empty">Carregando...</div>
          ) : filtered.length === 0 ? (
            <div className="empty">Nenhuma atividade encontrada.</div>
          ) : (
            <table className="table atv-table">
              <colgroup>
                <col style={{ width: 185 }} />
                <col style={{ width: 280 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 155 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 60 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 115 }} />
                <col style={{ width: 75 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Atividade</th>
                  <th>Responsável</th>
                  <th>Vendedor</th>
                  <th className="num">Valores</th>
                  <th className="num">Prazo</th>
                  <th>Retorno máx.</th>
                  <th>Data cliente</th>
                  <th>Situação</th>
                  <th>Status</th>
                  <th>Plano</th>
                  <th className="col-actions">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => {
                  const colab = row.colaborador_id ? colabMap.get(row.colaborador_id) : null;
                  const cp = ckpts.get(row.id);
                  return (
                    <tr
                      key={row.id}
                      className="row-clickable"
                      onDoubleClick={() => setCheckpointFor(row)}
                      title="Dois cliques para abrir o checkpoint"
                    >
                      <td className="atv-cliente">
                        <span className="atv-cliente-nome">
                          {row.cliente || "—"}
                          {row.origem === "OMIE" && (
                            <span className="atv-omie-tag" title={`Importada do OMIE${row.codigo_os ? ` (cód. ${row.codigo_os})` : ""}`}>
                              OMIE
                            </span>
                          )}
                        </span>
                        {row.cidade && <span className="atv-cliente-cidade">{row.cidade}</span>}
                      </td>
                      <td className="atv-desc">
                        <span title={row.nome}>{row.nome}</span>
                      </td>
                      <td>
                        {colab ? (
                          <div className="resp-cell">
                            <span className="resp-nome">{colab.nome}</span>
                            {colab.telefone && <span className="resp-sub">{colab.telefone}</span>}
                            {colab.email && <span className="resp-sub">{colab.email}</span>}
                          </div>
                        ) : (
                          <span className="ckpt-empty">—</span>
                        )}
                      </td>
                      <td className="atv-vend">
                        {row.vendedor ? (
                          <span className="atv-vend-nome" title={row.vendedor}>
                            {abreviarNome(row.vendedor)}
                          </span>
                        ) : (
                          <span className="ckpt-empty">—</span>
                        )}
                      </td>
                      <td className="num atv-valores">
                        <span className="atv-fat">{formatCurrency(row.valor_faturado)}</span>
                        {Number(row.valor_custo) > 0 && (
                          <span className="atv-custo">custo {formatCurrency(row.valor_custo)}</span>
                        )}
                      </td>
                      <td className="num">
                        {row.prazo_execucao_dias != null ? `${row.prazo_execucao_dias} d` : "—"}
                      </td>
                      <td className="nowrap">{formatDate(row.data_retorno_maximo)}</td>
                      <td className="nowrap">{formatDate(row.data_cliente)}</td>
                      <td>
                        {row.situacao ? (
                          <span className={`pill pill-${statusTone(row.situacao)}`}>{row.situacao}</span>
                        ) : (
                          <span className="ckpt-empty">—</span>
                        )}
                      </td>
                      <td>
                        {row.status ? (
                          <span className={`pill pill-${statusTone(row.status)}`}>{row.status}</span>
                        ) : (
                          <span className="ckpt-empty">—</span>
                        )}
                      </td>
                      <td>
                        {!cp ? (
                          <span className="ckpt-empty">—</span>
                        ) : cp.opcao_escolhida === "confirmada" ? (
                          <span className="plan-pill conf" title="Data confirmada com o cliente">
                            <IconCheck size={12} /> Confirmada
                          </span>
                        ) : cp.opcao_escolhida === "manual" ? (
                          <span className="plan-pill conf" title="Data marcada pelo Planejamento">
                            <IconCheck size={12} /> Data do Planejamento
                          </span>
                        ) : cp.opcao_escolhida === "pre" ? (
                          <span className="plan-pill pre" title="3 datas pré-agendadas (aguardando cliente)">
                            Pré-agendada
                          </span>
                        ) : (
                          <span className="ckpt-ok" title="Checkpoint preenchido">
                            <IconCheck size={14} /> OK
                          </span>
                        )}
                      </td>
                      <td
                        className="col-actions"
                        onClick={(e) => e.stopPropagation()}
                        onDoubleClick={(e) => e.stopPropagation()}
                      >
                        <button className="icon-btn" title="Editar" onClick={() => openEdit(row)}>
                          <IconEdit size={16} />
                        </button>
                        {podeExcluir && (
                          <button
                            className="icon-btn icon-btn-danger"
                            title="Excluir"
                            onClick={() => handleDelete(row)}
                          >
                            <IconTrash size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {formOpen && (
        <RecordForm
          title={editing ? "Editar atividade" : "Nova atividade"}
          fields={tab.fields}
          initial={editing}
          refs={refs}
          onCancel={() => setFormOpen(false)}
          onSubmit={handleSubmit}
        />
      )}

      {checkpointFor && (
        <CheckpointModal
          atividade={checkpointFor}
          colaborador={checkpointFor.colaborador_id ? colabMap.get(checkpointFor.colaborador_id) : null}
          onClose={() => setCheckpointFor(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}

/* -------------------------------- Kanban -------------------------------- */

/** "dd/mm" (em UTC, como o formatDate) — cabe na linha curta do cartão. */
function diaMes(value: unknown): string {
  const d = new Date(value as string);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("pt-BR", { timeZone: "UTC", day: "2-digit", month: "2-digit" });
}

/** O que a coluna "Plano" da lista mostra, em texto curto para o cartão. */
function planoDoCheckpoint(
  cp: CkptResumo | undefined,
): { texto: string; titulo: string; feito: boolean } | null {
  if (!cp) return null;
  if (cp.opcao_escolhida === "confirmada")
    return { texto: "Confirmada", titulo: "Data confirmada com o cliente", feito: true };
  if (cp.opcao_escolhida === "manual")
    return { texto: "Data do Planejamento", titulo: "Data marcada pelo Planejamento", feito: true };
  // Pré-agendada ainda espera o cliente: sem o ✓, como na lista.
  if (cp.opcao_escolhida === "pre")
    return { texto: "Pré-agendada", titulo: "3 datas pré-agendadas (aguardando cliente)", feito: false };
  return { texto: "Checkpoint OK", titulo: "Checkpoint preenchido", feito: true };
}

/**
 * O QUADRO — 4 colunas, arrastar e soltar nativo do navegador (HTML5).
 *
 * Fica FORA do AtividadesView de propósito: componente declarado dentro de
 * outro é recriado a cada digitação na busca, e o quadro inteiro remontaria.
 * O arraste é guardado num ref que o pai também enxerga — é por ele que a
 * atualização de 60 s sabe que precisa esperar.
 */
function QuadroKanban({
  rows,
  carregando,
  colabMap,
  ckpts,
  podeGravar,
  podeExcluir,
  arrastandoRef,
  onMover,
  onEditar,
  onExcluir,
  onCheckpoint,
}: {
  rows: Row[];
  carregando: boolean;
  colabMap: Map<number, Row>;
  ckpts: Map<number, CkptResumo>;
  /** false = somente visualização: nada de arrastar, mover, editar ou excluir. */
  podeGravar: boolean;
  podeExcluir: boolean;
  arrastandoRef: MutableRefObject<number | null>;
  onMover: (id: number, coluna: ColunaKanban) => void;
  onEditar: (row: Row) => void;
  onExcluir: (row: Row) => void;
  onCheckpoint: (row: Row) => void;
}) {
  /** Só para o visual (cartão apagado / coluna destacada); a lógica usa o ref. */
  const [arrastando, setArrastando] = useState<number | null>(null);
  const [alvo, setAlvo] = useState<ColunaKanban | null>(null);

  const porColuna = useMemo(() => {
    const m = new Map<ColunaKanban, Row[]>(COLUNAS_KANBAN.map((c) => [c.key, [] as Row[]]));
    for (const r of rows) m.get(colunaDaAtividade(r))?.push(r);
    return m;
  }, [rows]);

  function comecarArraste(e: DragEvent<HTMLElement>, id: number) {
    e.dataTransfer.effectAllowed = "move";
    // Firefox só inicia o arraste com algum dado definido.
    e.dataTransfer.setData("text/plain", String(id));
    arrastandoRef.current = id;
    setArrastando(id);
  }

  function terminarArraste() {
    arrastandoRef.current = null;
    setArrastando(null);
    setAlvo(null);
  }

  if (carregando) {
    return (
      <div className="table-wrap">
        <div className="empty">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="kb-quadro">
      {COLUNAS_KANBAN.map((col) => {
        const cartoes = porColuna.get(col.key) ?? [];
        const total = cartoes.reduce((s, r) => s + (Number(r.valor_faturado) || 0), 0);
        return (
          <section
            key={col.key}
            className={`kb-coluna${alvo === col.key ? " alvo" : ""}`}
            aria-label={col.label}
            onDragOver={(e) => {
              // Só aceita cartão DESTE quadro — texto ou arquivo arrastado de
              // fora não vira "mudança de etapa".
              if (!podeGravar || arrastandoRef.current === null) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              if (alvo !== col.key) setAlvo(col.key);
            }}
            onDragLeave={(e) => {
              // O dragleave dispara a cada cartão por cima do qual o ponteiro
              // passa: só apaga o destaque quando ele sai da coluna de verdade.
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                setAlvo((a) => (a === col.key ? null : a));
              }
            }}
            onDrop={(e) => {
              e.preventDefault();
              const id = arrastandoRef.current;
              terminarArraste();
              if (podeGravar && id !== null) onMover(id, col.key);
            }}
          >
            <header className="kb-coluna-topo">
              <div className="kb-coluna-titulo">
                <strong>{col.label}</strong>
                <span className="kb-contagem" title={`${cartoes.length} atividade(s)`}>
                  {cartoes.length}
                </span>
              </div>
              <span className="kb-coluna-total" title="Soma do valor faturado da coluna">
                {formatCurrency(total)}
              </span>
            </header>
            <div className="kb-coluna-corpo">
              {cartoes.length === 0 ? (
                <div className="kb-vazio">Nenhuma atividade</div>
              ) : (
                cartoes.map((row) => (
                  <CartaoKanban
                    key={row.id}
                    row={row}
                    coluna={col.key}
                    colab={row.colaborador_id ? colabMap.get(row.colaborador_id) : undefined}
                    cp={ckpts.get(row.id)}
                    podeGravar={podeGravar}
                    podeExcluir={podeExcluir}
                    arrastando={arrastando === row.id}
                    onDragStart={(e) => comecarArraste(e, row.id)}
                    onDragEnd={terminarArraste}
                    onMover={onMover}
                    onEditar={onEditar}
                    onExcluir={onExcluir}
                    onCheckpoint={onCheckpoint}
                  />
                ))
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}

/**
 * Um cartão do quadro. Hierarquia pelo tamanho/peso da letra, não pela cor:
 * no máximo UM destaque (a data limite vencida ou vencendo hoje).
 */
function CartaoKanban({
  row,
  coluna,
  colab,
  cp,
  podeGravar,
  podeExcluir,
  arrastando,
  onDragStart,
  onDragEnd,
  onMover,
  onEditar,
  onExcluir,
  onCheckpoint,
}: {
  row: Row;
  coluna: ColunaKanban;
  colab: Row | undefined;
  cp: CkptResumo | undefined;
  podeGravar: boolean;
  podeExcluir: boolean;
  arrastando: boolean;
  onDragStart: (e: DragEvent<HTMLElement>) => void;
  onDragEnd: () => void;
  onMover: (id: number, coluna: ColunaKanban) => void;
  onEditar: (row: Row) => void;
  onExcluir: (row: Row) => void;
  onCheckpoint: (row: Row) => void;
}) {
  const plano = planoDoCheckpoint(cp);
  const alerta = row.situacao === "Atrasada" || row.situacao === "Vence hoje" ? row.situacao : null;
  const parar = (e: { stopPropagation: () => void }) => e.stopPropagation();

  return (
    <article
      className={`kb-cartao${arrastando ? " arrastando" : ""}`}
      draggable={podeGravar}
      onDragStart={podeGravar ? onDragStart : undefined}
      onDragEnd={podeGravar ? onDragEnd : undefined}
      onDoubleClick={() => onCheckpoint(row)}
      title="Dois cliques para abrir o checkpoint"
    >
      <div className="kb-cartao-topo">
        <span className="kb-cliente">
          {row.cliente || "—"}
          {row.origem === "OMIE" && (
            <span className="atv-omie-tag" title={`Importada do OMIE${row.codigo_os ? ` (cód. ${row.codigo_os})` : ""}`}>
              OMIE
            </span>
          )}
        </span>
        {plano && (
          <span className="kb-plano" title={plano.titulo}>
            {plano.feito && <IconCheck size={11} />} {plano.texto}
          </span>
        )}
      </div>

      <p className="kb-nome" title={row.nome}>
        {row.nome}
      </p>
      {row.cidade && <span className="kb-cidade">{row.cidade}</span>}

      <div className="kb-pessoas">
        <span title={colab ? `Responsável técnico: ${colab.nome}` : "Sem responsável técnico"}>
          <span className="kb-rotulo">Resp.</span> {colab?.nome ?? "—"}
        </span>
        <span title={row.vendedor ? `Vendedor: ${row.vendedor}` : "Sem vendedor"}>
          <span className="kb-rotulo">Vend.</span> {row.vendedor ? abreviarNome(row.vendedor) : "—"}
        </span>
      </div>

      <div className="kb-prazo">
        <span>
          {row.data_cliente ? (
            <>
              Data limite: <strong>{formatDate(row.data_cliente)}</strong>
            </>
          ) : (
            "Sem data limite"
          )}
        </span>
        {alerta && <span className="kb-situacao">{alerta}</span>}
        {row.data_retorno_maximo && <span>Retorno máx. {diaMes(row.data_retorno_maximo)}</span>}
      </div>

      <div className="kb-cartao-rodape">
        <span className="kb-valor">{formatCurrency(row.valor_faturado)}</span>
        {podeGravar && (
          <div className="kb-acoes" onClick={parar} onDoubleClick={parar}>
            {/* Celular/iPhone não arrasta (o HTML5 não funciona no toque): a
                troca de etapa pelo menu vale para qualquer tela. */}
            <select
              className="kb-mover"
              data-grava
              value=""
              aria-label="Mover para outra etapa"
              title="Mover para outra etapa"
              onChange={(e) => {
                const destino = e.target.value;
                if (ehColunaKanban(destino)) onMover(row.id, destino);
              }}
            >
              <option value="">Mover para…</option>
              {COLUNAS_KANBAN.filter((c) => c.key !== coluna).map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
            </select>
            <button className="icon-btn" title="Editar" onClick={() => onEditar(row)}>
              <IconEdit size={15} />
            </button>
            {podeExcluir && (
              <button className="icon-btn icon-btn-danger" title="Excluir" onClick={() => onExcluir(row)}>
                <IconTrash size={15} />
              </button>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

/* ------------------------------ Checkpoint ------------------------------ */

const SIM_NAO = ["Sim", "Não"];
/** Veículos do checkpoint: dá para marcar os dois (ex.: "Carro, Caminhão"). */
const VEICULOS = ["Carro", "Caminhão"];
const REGIMES = ["Dias de semana", "Somente fim de semana", "Ambos"];

function CheckpointModal({
  atividade,
  colaborador,
  onClose,
  onSaved,
}: {
  atividade: Row;
  colaborador: Row | null | undefined;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<Row>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [acao, setAcao] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [opcoes, setOpcoes] = useState<Opcao[]>([]);
  const [opcaoEscolhida, setOpcaoEscolhida] = useState<string | null>(null);
  const [dataConfirmada, setDataConfirmada] = useState<string | null>(null);
  /** Mensagem do servidor sobre o envio para o Gestor Operacional. */
  const [avisoAprovacao, setAvisoAprovacao] = useState<string | null>(null);
  const [verificandoDoc, setVerificandoDoc] = useState(false);
  const [docInfo, setDocInfo] = useState<DocInfo | null>(null);

  /* --- Opção 03: a data que o próprio Planejamento marca --- */
  /** A data digitada no campo (AAAA-MM-DD, formato do <input type="date">). */
  const [dataManual, setDataManual] = useState("");
  const [verificando, setVerificando] = useState(false);
  /** O que a IA respondeu sobre essa data: janela + quem está livre nela. */
  const [analise, setAnalise] = useState<VerificacaoData | null>(null);
  /** Ids marcados para executar (começa na sugestão e o usuário pode trocar). */
  const [equipeManual, setEquipeManual] = useState<number[]>([]);
  /** Texto do conflito devolvido pelo servidor (equipe ocupada / data passada). */
  const [conflito, setConflito] = useState<string | null>(null);
  /** O que a IA leu na última consulta (equipe, agenda, pedidos pendentes). */
  const [leitura, setLeitura] = useState<LeituraIA | null>(null);

  const set = (name: string, value: unknown) => setForm((p) => ({ ...p, [name]: value }));

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.checkpoint
      .get(atividade.id)
      .then((cp) => {
        if (!alive) return;
        setForm(cp ?? {});
        setOpcoes(lerOpcoes(cp?.opcoes_data));
        setOpcaoEscolhida(cp?.opcao_escolhida ?? null);
        setDataConfirmada(cp?.data_confirmada ?? null);
        // Reabrir o checkpoint mostra a data escolhida à mão do jeito que ela
        // foi enviada — sem isso o campo voltava vazio e parecia não ter salvo.
        const manual = lerOpcaoManual(cp?.opcao_manual);
        if (manual?.inicio) {
          setDataManual(manual.inicio.slice(0, 10));
          setEquipeManual((manual.equipe ?? []).map((m) => Number(m.id)));
        }
      })
      .catch(() => alive && setForm({}))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [atividade.id]);

  async function salvar(): Promise<boolean> {
    setSaving(true);
    setError(null);
    try {
      await api.checkpoint.save(atividade.id, {
        tipo_servico: form.tipo_servico ?? "",
        descricao_servico: form.descricao_servico ?? "",
        dias_viagem: form.dias_viagem ?? "",
        dias_servico: form.dias_servico ?? "",
        ponto_energia: form.ponto_energia ?? "",
        necessario_escada: form.necessario_escada ?? "",
        veiculo: form.veiculo ?? "",
        dias_semana: form.dias_semana ?? "",
        documentos: form.documentos ?? "",
        qtd_colaboradores: form.qtd_colaboradores ?? "",
        ensaios_eletricos: form.ensaios_eletricos ?? "",
      });
      onSaved();
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Erro ao salvar o checkpoint");
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function gerarOpcoes() {
    const ok = await salvar(); // a data depende das respostas
    if (!ok) return;
    setGerando(true);
    setError(null);
    setOpcaoEscolhida(null);
    try {
      const r = await api.planejamento.opcoesData(atividade.id);
      setOpcoes((r.opcoes ?? []) as Opcao[]);
      if (r.leitura) setLeitura(r.leitura);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Erro ao gerar as datas");
    } finally {
      setGerando(false);
    }
  }

  async function preAgendarTudo() {
    setAcao(true);
    setError(null);
    try {
      const r = await api.planejamento.preAgendar(atividade.id);
      setOpcaoEscolhida("pre");
      setDataConfirmada(null);
      setAvisoAprovacao(r?.mensagem ?? null);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Erro ao pré-agendar");
    } finally {
      setAcao(false);
    }
  }

  async function confirmarOpcao(indice: number) {
    setAcao(true);
    setError(null);
    try {
      const r = await api.planejamento.confirmarData(atividade.id, indice);
      setOpcaoEscolhida("confirmada");
      setDataConfirmada(r?.escolhida?.inicio ?? null);
      setAvisoAprovacao(r?.mensagem ?? null);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Erro ao confirmar a data");
    } finally {
      setAcao(false);
    }
  }

  /**
   * Opção 03 — pergunta à IA o que acontece SE o serviço for nessa data.
   * Salva o checkpoint antes porque a janela depende dos dias e do regime que
   * estão no formulário (mudar "dias de serviço" muda o intervalo inteiro).
   */
  async function verificarDataManual() {
    if (!dataManual) return;
    const ok = await salvar();
    if (!ok) return;
    setVerificando(true);
    setError(null);
    setConflito(null);
    try {
      const r = await api.planejamento.verificarData(atividade.id, dataManual);
      setAnalise(r);
      setLeitura(r.leitura);
      // Só sugere a equipe quando o usuário ainda não escolheu ninguém: se ele
      // já marcou gente, trocar a data não pode apagar a escolha dele.
      setEquipeManual((atual) => (atual.length ? atual : r.sugestao.map((p) => p.id)));
    } catch (err) {
      setAnalise(null);
      setError(err instanceof ApiError ? err.message : "Erro ao verificar a data");
    } finally {
      setVerificando(false);
    }
  }

  /** Envia a data escolhida à mão para a aprovação do Gestor Operacional. */
  async function enviarDataManual(forcar = false) {
    if (!dataManual) return;
    setAcao(true);
    setError(null);
    setConflito(null);
    try {
      const r = await api.planejamento.agendarData(atividade.id, dataManual, equipeManual, forcar);
      if ("conflito" in r && r.conflito) {
        // Não é erro: é a IA avisando o choque. A tela pergunta se envia assim
        // mesmo, e o segundo clique manda com `forcar`.
        setAnalise(r);
        setConflito(r.mensagem);
        return;
      }
      setOpcaoEscolhida("manual");
      setDataConfirmada(r.escolhida?.inicio ?? dataManual);
      setAvisoAprovacao(r.mensagem ?? null);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Erro ao enviar a data");
    } finally {
      setAcao(false);
    }
  }

  /** Marca/desmarca um colaborador da equipe da data escolhida. */
  function alternarMembro(id: number) {
    setConflito(null);
    setEquipeManual((atual) =>
      atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id],
    );
  }

  async function verificarDocumentos() {
    const ok = await salvar(); // garante os documentos salvos
    if (!ok) return;
    setVerificandoDoc(true);
    setError(null);
    try {
      // sem override: usa o responsável técnico atual da atividade (que a IA atribui).
      setDocInfo(await api.planejamento.documentos(atividade.id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Erro ao verificar documentos");
    } finally {
      setVerificandoDoc(false);
    }
  }

  async function salvarEFechar() {
    if (await salvar()) onClose();
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card modal-wide ckpt-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Checkpoint da atividade</h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="ckpt-context">
            <strong>{atividade.nome}</strong>
            <span className="muted">
              {atividade.cliente || "—"}
              {atividade.cidade ? ` · ${atividade.cidade}` : ""}
              {colaborador ? ` · Resp.: ${colaborador.nome}` : ""}
            </span>
          </div>

          {loading ? (
            <div className="empty">Carregando checkpoint...</div>
          ) : (
            <>
              <div className="form-grid">
                <label className="field field-wide">
                  <span>Tipo de serviço</span>
                  <input
                    type="text"
                    value={form.tipo_servico ?? ""}
                    placeholder="Ex.: Manutenção preventiva em subestação"
                    onChange={(e) => set("tipo_servico", e.target.value)}
                  />
                </label>

                <label className="field field-wide">
                  <span>Descrição do serviço</span>
                  <textarea
                    rows={4}
                    value={form.descricao_servico ?? ""}
                    placeholder="Cole aqui o escopo do serviço (proposta / OS)"
                    onChange={(e) => set("descricao_servico", e.target.value)}
                  />
                </label>

                <label className="field">
                  <span>Dias de viagem</span>
                  <input
                    type="number"
                    min="0"
                    value={form.dias_viagem ?? ""}
                    onChange={(e) => set("dias_viagem", e.target.value)}
                  />
                </label>

                <label className="field">
                  <span>Dias de serviço</span>
                  <input
                    type="number"
                    min="0"
                    value={form.dias_servico ?? ""}
                    onChange={(e) => set("dias_servico", e.target.value)}
                  />
                </label>

                {/* O agendamento (IA e sugestão de data) trabalha com o TOTAL. */}
                <div className="field field-wide ckpt-total">
                  Total para agendar: <strong>{totalDias(form)} dia(s)</strong>
                  <span className="muted"> — viagem + serviço</span>
                </div>

                <Selecao label="Ponto de energia?" value={form.ponto_energia} options={SIM_NAO} onChange={(v) => set("ponto_energia", v)} />
                <Selecao label="Escada?" value={form.necessario_escada} options={SIM_NAO} onChange={(v) => set("necessario_escada", v)} />

                <VeiculoInput value={form.veiculo} onChange={(v) => set("veiculo", v)} />

                <Selecao label="Dias úteis ou final de semana?" value={form.dias_semana} options={REGIMES} onChange={(v) => set("dias_semana", v)} />

                <label className="field field-wide">
                  <span>Documentos necessários</span>
                  <textarea
                    rows={2}
                    value={form.documentos ?? ""}
                    placeholder="ART, PT, ASO, NRs, liberações..."
                    onChange={(e) => set("documentos", e.target.value)}
                  />
                </label>

                <label className="field">
                  <span>Colaboradores</span>
                  <input
                    type="number"
                    min="0"
                    value={form.qtd_colaboradores ?? ""}
                    onChange={(e) => set("qtd_colaboradores", e.target.value)}
                  />
                </label>

                <Selecao label="Ensaios elétricos?" value={form.ensaios_eletricos} options={SIM_NAO} onChange={(v) => set("ensaios_eletricos", v)} />
              </div>

              {/* IA: gera 3 opções de data a partir de skills + serviço + agenda + dias */}
              <div className="ckpt-block">
                <div className="ckpt-block-head">
                  <div>
                    <strong>Opções de data (IA)</strong>
                    <span className="muted"> — skills da equipe + serviço + agenda + dias</span>
                  </div>
                  {/* `modo-leitura-esconde`: gerar/enviar datas é gravação — some
                      para quem é somente visualização (ver styles.css). */}
                  <button className="btn-secondary modo-leitura-esconde" onClick={gerarOpcoes} disabled={saving || gerando || acao}>
                    <IconCalendar size={16} /> {gerando ? "Gerando..." : "Gerar 3 opções de data"}
                  </button>
                </div>

                {/* O que a IA leu de verdade — sem isso, "gerado por IA" é
                    palavra: aqui o usuário vê o tamanho da base consultada. */}
                {leitura && (
                  <div className="ia-leitura">
                    A IA leu {leitura.colaboradores_ativos} pessoa(s) da equipe e{" "}
                    {leitura.marcacoes_agenda} marcação(ões) na agenda
                    {leitura.pedidos_pendentes > 0
                      ? `, mais ${leitura.pedidos_pendentes} pedido(s) de outras atividades aguardando o gestor`
                      : ""}
                    .
                  </div>
                )}

                {opcoes.length > 0 && (
                  <>
                    <div className="opc-grid">
                      {opcoes.map((o, i) => (
                        <div
                          key={i}
                          className={`opc-card${opcaoEscolhida === "confirmada" && dataConfirmada === o.inicio ? " sel" : ""}`}
                        >
                          <span className="opc-num">Opção {i + 1}</span>
                          <strong className="opc-data">{o.rotulo}</strong>
                          <span className="opc-equipe" title={o.equipe.map((m) => m.nome).join(", ")}>
                            {o.equipe.map((m) => abreviarNome(m.nome)).join(", ")}
                          </span>
                          <button className="btn-mini" disabled={acao} onClick={() => confirmarOpcao(i)}>
                            <IconCheck size={13} /> Confirmar c/ cliente
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="opc-acoes modo-leitura-esconde">
                      <button className="btn-secondary" disabled={acao} onClick={preAgendarTudo}>
                        Opção 01 — Enviar as 3 para aprovação (aguardar cliente)
                      </button>
                      <span className="muted">Opção 02 — confirmar uma data acima</span>
                    </div>
                  </>
                )}

                {/* A agenda NÃO é mais marcada aqui: o pedido vai para a aba
                    "Aprovar Agendamento" do Gestor Operacional, e é a aprovação
                    dele que ocupa os dias da equipe. */}
                {opcaoEscolhida === "pre" && (
                  <div className="ckpt-status">
                    <IconCheck size={14} /> 3 datas enviadas ao Gestor Operacional — a Agenda é
                    marcada quando ele aprovar.
                  </div>
                )}
                {opcaoEscolhida === "confirmada" && (
                  <div className="ckpt-status">
                    <IconCheck size={14} /> Data confirmada com o cliente
                    {dataConfirmada ? `: ${formatDate(dataConfirmada)}` : ""} — enviada ao Gestor
                    Operacional para aprovação.
                  </div>
                )}
                {avisoAprovacao && <div className="ckpt-aprovacao">{avisoAprovacao}</div>}
              </div>

              {/* Opção 03: a data é do Planejamento, a leitura da agenda é da IA.
                  As 3 opções acima vêm da primeira janela livre da equipe — mas a
                  data de um serviço costuma vir do cliente, da parada da fábrica
                  ou da viagem já comprada. Aqui o usuário digita o dia e a IA
                  responde quem está livre nele, em vez de escolher por ele. */}
              <div className="ckpt-block">
                <div className="ckpt-block-head">
                  <div>
                    <strong>Data escolhida pelo Planejamento</strong>
                    <span className="muted"> — você marca o dia; a IA mostra quem está livre</span>
                  </div>
                  <div className="dm-escolha modo-leitura-esconde">
                    <input
                      type="date"
                      value={dataManual}
                      onChange={(e) => {
                        setDataManual(e.target.value);
                        setAnalise(null);
                        setConflito(null);
                      }}
                    />
                    <button
                      className="btn-secondary"
                      onClick={verificarDataManual}
                      disabled={!dataManual || saving || verificando || acao}
                    >
                      <IconSearch size={15} />{" "}
                      {verificando ? "Verificando..." : "Verificar disponibilidade"}
                    </button>
                  </div>
                </div>

                {analise && (
                  <>
                    <div className="dm-resumo">
                      <span className="dm-janela">
                        <IconCalendar size={14} /> {analise.rotulo}
                      </span>
                      <span className="muted">
                        {analise.dias} dia(s) · {analise.regime} · {analise.livres} de{" "}
                        {analise.pessoas.length} livre(s) · o checkpoint pede {analise.qtd}
                      </span>
                    </div>

                    {analise.avisos.map((a, i) => (
                      <div key={i} className="dm-aviso">
                        {a}
                      </div>
                    ))}

                    {/* Quem vai. Começa na sugestão (livres + afinidade com o
                        serviço) e o usuário troca à vontade — inclusive
                        marcando alguém ocupado, com o choque à vista. */}
                    <div className="dm-pessoas">
                      {analise.pessoas.map((p) => (
                        <label
                          key={p.id}
                          className={`dm-pessoa${p.livre ? "" : " ocupado"}${
                            equipeManual.includes(p.id) ? " sel" : ""
                          }`}
                          title={
                            p.livre
                              ? "Livre em todos os dias dessa janela"
                              : p.conflitos
                                  .map((c) => `${formatDate(c.data)} — ${c.motivo}`)
                                  .join("\n")
                          }
                        >
                          <input
                            type="checkbox"
                            checked={equipeManual.includes(p.id)}
                            onChange={() => alternarMembro(p.id)}
                          />
                          <span className="dm-nome">{abreviarNome(p.nome)}</span>
                          {p.livre ? (
                            <span className="dm-tag livre">livre</span>
                          ) : (
                            <span className="dm-tag ocup">
                              ocupado {p.conflitos[0] ? formatDate(p.conflitos[0].data) : ""}
                            </span>
                          )}
                        </label>
                      ))}
                    </div>

                    {conflito && <div className="dm-conflito">{conflito}</div>}

                    <div className="opc-acoes">
                      <button
                        className="btn-secondary"
                        disabled={acao || equipeManual.length === 0}
                        onClick={() => enviarDataManual(!!conflito)}
                      >
                        <IconCheck size={14} />{" "}
                        {conflito
                          ? "Enviar assim mesmo ao Gestor Operacional"
                          : "Usar esta data e enviar para aprovação"}
                      </button>
                      <span className="muted">
                        {equipeManual.length} colaborador(es) marcado(s) — a agenda só é ocupada
                        depois do aval do gestor.
                      </span>
                    </div>
                  </>
                )}

                {opcaoEscolhida === "manual" && (
                  <div className="ckpt-status">
                    <IconCheck size={14} /> Data escolhida pelo Planejamento
                    {dataConfirmada ? `: ${formatDate(dataConfirmada)}` : ""} — enviada ao Gestor
                    Operacional para aprovação.
                  </div>
                )}
              </div>

              {/* Integração com o DP: documentos do colaborador */}
              <div className="ckpt-block">
                <div className="ckpt-block-head">
                  <div>
                    <strong>Documentos (integração DP)</strong>
                    <span className="muted"> — busca na pasta do colaborador no DP</span>
                  </div>
                  <button className="btn-secondary" onClick={verificarDocumentos} disabled={saving || verificandoDoc}>
                    {verificandoDoc ? "Verificando..." : "Verificar / montar pasta"}
                  </button>
                </div>
                {docInfo && (
                  <div className="doc-result">
                    <div className={`doc-msg${docInfo.faltando && docInfo.faltando.length ? " falta" : " ok"}`}>
                      {docInfo.mensagem}
                    </div>
                    {docInfo.presentes && docInfo.presentes.length > 0 && (
                      <div className="doc-line">
                        <span className="doc-tag ok">Na pasta</span> {docInfo.presentes.map((p) => p.doc).join(", ")}
                      </div>
                    )}
                    {docInfo.faltando && docInfo.faltando.length > 0 && (
                      <div className="doc-line">
                        <span className="doc-tag falta">Faltando</span> {docInfo.faltando.join(", ")}
                      </div>
                    )}
                    {docInfo.ok && (
                      <a className="btn-primary doc-zip" href={api.planejamento.zipUrl(atividade.id)}>
                        <IconDownload size={15} /> Baixar pasta (.zip) p/ enviar ao cliente
                      </a>
                    )}
                  </div>
                )}
              </div>

              {error && <div className="form-error">{error}</div>}
            </>
          )}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Fechar
          </button>
          <button type="button" className="btn-primary" onClick={salvarEFechar} disabled={saving || loading}>
            {saving ? "Salvando..." : "Salvar checkpoint"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Total de dias a agendar = viagem + serviço. O servidor calcula o mesmo valor
 * ao salvar (é ele que a IA e a sugestão de data usam); aqui é só o espelho
 * na tela, para quem preenche ver o que vai ser reservado na agenda.
 */
function totalDias(form: Row): number {
  const n = (v: unknown) => {
    const x = Number(v);
    return Number.isFinite(x) && x > 0 ? Math.floor(x) : 0;
  };
  return n(form.dias_viagem) + n(form.dias_servico);
}

/**
 * Veículos necessários. Diferente das demais perguntas, aceita MAIS DE UM
 * (uma equipe pode levar carro e caminhão), então são caixas de marcação.
 * Gravado no mesmo campo TEXT, separado por vírgula: "Carro, Caminhão".
 */
function VeiculoInput({
  value,
  onChange,
}: {
  value: unknown;
  onChange: (v: string) => void;
}) {
  const marcados = String(value ?? "")
    .split(/[,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);

  const marcado = (v: string) => marcados.some((m) => m.toLowerCase() === v.toLowerCase());

  function alternar(v: string) {
    const proximos = marcado(v)
      ? marcados.filter((m) => m.toLowerCase() !== v.toLowerCase())
      : [...marcados, v];
    // Grava sempre na ordem de VEICULOS (evita "Caminhão, Carro" e "Carro, Caminhão").
    onChange(VEICULOS.filter((op) => proximos.some((p) => p.toLowerCase() === op.toLowerCase())).join(", "));
  }

  return (
    <div className="field">
      <span>Veículo</span>
      <div className="ckpt-checks">
        {VEICULOS.map((v) => (
          <label key={v} className="ckpt-check">
            <input type="checkbox" checked={marcado(v)} onChange={() => alternar(v)} />
            <span>{v}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function Selecao({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: unknown;
  options: string[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <select value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">Selecione...</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}
